import { Link } from 'react-router-dom';
import GuideLayout from '../../components/guides/GuideLayout';
import GuideCTA from '../../components/guides/GuideCTA';
import { GuideSection, GuideFaqItem, GuideChecklist, GuideNote, SourceLink } from '../../components/guides/GuideParts';

const SLUG = 'how-to-request-your-cafcass-and-family-court-records';

export const RequestCafcassCourtRecords = () => (
  <GuideLayout slug={SLUG}>
    <GuideSection>
      <p>
        If your case went to court — most care proceedings do — two more organisations sit
        alongside the council in your history: Cafcass, whose guardian spoke for your interests,
        and the family court itself, which decided what happened. They are easy to lump together,
        but they work completely differently. Cafcass will answer an ordinary Subject Access
        Request, the same as any other organisation. The court&apos;s own case file will not — it
        is governed by different rules built around the privacy of family proceedings, and
        getting at it is a genuinely different, harder process.
      </p>
    </GuideSection>

    <GuideSection eyebrow="The straightforward part" title="Cafcass: an ordinary SAR, with one distinctive feature">
      <p>
        Cafcass (the Children and Family Court Advisory and Support Service) is its own,
        separate data controller — not part of the council or the court — and it will handle a
        request from you the same as any other organisation. Its guardian&apos;s reports, welfare
        analysis, case notes and other case-related documents from your proceedings are exactly
        the kind of thing a SAR is for.
      </p>
      <GuideChecklist
        items={[
          <>
            Request via{' '}
            <SourceLink href="https://www.cafcass.gov.uk/about-us/transparency-information/subject-access-requests">
              Cafcass&apos;s own subject access request page
            </SourceLink>
            , by post to its Customer Services Team, or by phone. You will need to show proof of
            identity and address.
          </>,
          <>
            <strong className="text-ink">Retention runs to the youngest child in the
            case</strong> — Cafcass keeps case information until the youngest child involved
            turns 25, not from your own age. If your proceedings involved younger siblings, your
            own records may still exist well after your own 25th birthday, so it is always worth
            asking rather than assuming it has been destroyed.
          </>,
          "In Wales, CAFCASS Cymru is a separate body with its own request route — if your proceedings were in Wales, ask them directly rather than Cafcass (England).",
        ]}
      />
    </GuideSection>

    <GuideSection eyebrow="Where it gets harder" title="The court's own case file is a different regime">
      <p>
        The court that decided your case keeps its own file — the judgment, statements filed by
        every party, expert reports it commissioned, transcripts. This is not something an
        ordinary SAR reaches, because family proceedings are private by default and the{' '}
        <SourceLink href="https://www.legislation.gov.uk/uksi/2010/2955/rule/29.12">
          Family Procedure Rules 2010, rule 29.12
        </SourceLink>{' '}
        say plainly that no document held in the court office may be inspected by any person
        without the court&apos;s permission, except in specific circumstances set out in the rules.
      </p>
      <p>
        In practice, getting access is a balancing exercise the court carries out — weighing your
        reasons for wanting the material against the privacy interests it exists to protect,
        including other people&apos;s. That balance does not disappear just because you were the
        child at the centre of the case and are now an adult, but there is a genuine, unresolved
        practical question here we want to be honest about: whether you, as the former child
        subject of the proceedings, can simply ask to inspect the file as a former party under
        rule 29.12(3), or whether you need a fresh application for the court&apos;s permission years
        later. We could not find a clear, definitive answer either way, and it may come down to
        the specific court. The usual route people use for this kind of application is Form C2
        (an application for directions in existing or concluded family proceedings), but treat
        that as a starting point to ask about, not a guarantee.
      </p>
      <GuideNote>
        <p>
          <strong>It may simply no longer exist.</strong> Court files are not kept forever, and we
          could not find a single published retention period for family court case files.
          Especially for older proceedings, it is worth asking early rather than assuming the file
          is still there to request.
        </p>
      </GuideNote>
    </GuideSection>

    <GuideCTA
      heading="Start with your council request — it covers more than you'd expect"
      body="Add the council to Precious Data and we'll draft your Subject Access Request — which reaches most of what the court file would contain, without needing a separate court application."
    />

    <GuideSection eyebrow="The practical shortcut" title="Try the council SAR first — it covers more than you'd think">
      <p>
        Before pursuing a court application, it is worth being clear about what a normal council
        SAR already gets you. Rule 29.12 only restricts inspecting the court&apos;s own copy, held in
        the court office. It says nothing about the local authority&apos;s own copies of the
        documents it drafted and then filed with the court — its care plan, its social workers&apos;
        statements, its chronology of your life. Those are the council&apos;s own records about you,
        and they are reachable through an ordinary{' '}
        <Link to="/guides/how-to-request-your-care-records-from-the-council" className="text-brand underline hover:opacity-90">
          Subject Access Request to the council
        </Link>
        , the same as anything else in your file — no court application needed.
      </p>
      <p>
        That leaves the court-file route mattering mainly for what is genuinely{' '}
        <strong className="text-ink">court-only</strong>: the judge&apos;s final judgment, statements
        filed by other parties (a parent&apos;s own statement, for instance), reports from experts the
        court itself commissioned, and any transcript of a hearing. Work out what you actually
        still need after your council and Cafcass requests come back, rather than starting with
        the hardest route.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Getting help" title="This is worth doing with support">
      <p>
        A court-file application is a genuinely more technical step than writing a SAR letter.{' '}
        <SourceLink href="https://frg.org.uk">Family Rights Group</SourceLink> and{' '}
        <SourceLink href="https://childrenslegalcentre.com">
          Coram Children&apos;s Legal Centre
        </SourceLink>{' '}
        both advise on family court matters and are a reasonable place to start if you want to
        pursue this route; <SourceLink href="https://becomecharity.org.uk">Become</SourceLink>, the
        national charity for care leavers, can also help you think through what you actually want
        before you decide how far to take it.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Common questions" title="Questions people ask us most" tinted>
      <div className="space-y-2" data-testid="cafcass-court-faq">
        <GuideFaqItem
          q="Do I need the court file at all, or does the council request cover what I actually want?"
          a="For most people, the council SAR alone gets you the fuller, more personal picture — day-to-day case notes, assessments, the story of decisions as they were made. The court file tends to matter most if you specifically want the judge's own reasoning, or a document filed by someone other than the council, like a parent's statement."
        />
        <GuideFaqItem
          q="What if I don't know which court my case was heard in?"
          a="The council that brought the proceedings will usually know, or can be asked as part of your SAR to them — it's a fair, specific question to put to them directly."
        />
        <GuideFaqItem
          q="Is Cafcass involved in every care case?"
          a="Usually, yes, in England — a Cafcass guardian is appointed in the great majority of public law care proceedings, so it's worth requesting from them even if you don't clearly remember a guardian being involved."
        />
        <GuideFaqItem
          q="Can I request records from the court about a sibling's case that affected mine?"
          a="Only your own personal data is squarely your right under a SAR — records specifically about a sibling's own case are their own personal data, not yours, even if the proceedings overlapped. Ask about anything filed jointly or that plainly concerns you both."
        />
      </div>
    </GuideSection>

    <GuideSection eyebrow="Read more" title="Where this comes from">
      <p>
        Cafcass&apos;s process and retention period are on its own{' '}
        <SourceLink href="https://www.cafcass.gov.uk/about-us/transparency-information/subject-access-requests">
          subject access request page
        </SourceLink>
        . The rules restricting access to a family court&apos;s own file are in{' '}
        <SourceLink href="https://www.legislation.gov.uk/uksi/2010/2955/rule/29.12">
          rule 29.12 of the Family Procedure Rules 2010
        </SourceLink>
        . For everything a normal council request covers, see our{' '}
        <Link to="/guides/how-to-request-your-care-records-from-the-council" className="text-brand underline hover:opacity-90">
          guide to requesting care records from the council
        </Link>
        .
      </p>
    </GuideSection>

    <GuideCTA
      heading="We handle the council and Cafcass side"
      body="Precious Data drafts and tracks Subject Access Requests to the council and to organisations like Cafcass — the court-file application is a separate, more specialist step, but everything else stays organised in one place while you decide whether you need it."
      testIdPrefix="guide-cta-end"
    />
  </GuideLayout>
);

export default RequestCafcassCourtRecords;
